import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@ouropix/ui';
import { Badge } from '@ouropix/ui';
import { Folder, FolderOpen } from 'lucide-react';
import { api } from '@/lib/api';
import { FolderTree } from '@/components/media/FolderTree';
import { useState } from 'react';

export const Route = createFileRoute('/_admin/media/pastas')({
  component: PastasPage,
});

interface MediaFolder {
  id: string;
  name: string;
  parent_id: string | null;
  created_at: string;
}

function PastasPage() {
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);

  const { data: folders = [] } = useQuery<MediaFolder[]>({
    queryKey: ['media-folders'],
    queryFn: async () => {
      const { data } = await api.get('/media/folders');
      // Backend retorna { folders: [...] }. Garantir array final.
      if (Array.isArray(data?.folders)) return data.folders;
      if (Array.isArray(data?.items)) return data.items;
      if (Array.isArray(data)) return data;
      return [];
    },
  });

  const selectedFolder = folders.find((f) => f.id === selectedFolderId);
  const children = folders.filter((f) => f.parent_id === selectedFolderId);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <FolderOpen className="h-5 w-5 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">Gerenciar Pastas</h1>
          <p className="text-muted-foreground text-sm">
            Organize sua biblioteca de mídias em pastas
          </p>
        </div>
      </div>

      <div className="flex gap-6">
        <div className="w-64 shrink-0">
          <Card>
            <CardContent className="p-0">
              <FolderTree
                folders={folders}
                currentFolderId={selectedFolderId}
                onSelect={setSelectedFolderId}
              />
            </CardContent>
          </Card>
        </div>

        <div className="flex-1">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Folder className="h-4 w-4" />
                {selectedFolder ? selectedFolder.name : 'Raiz'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {children.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  Nenhuma subpasta nesta localização.
                </p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {children.map((folder) => (
                    <button
                      key={folder.id}
                      onClick={() => setSelectedFolderId(folder.id)}
                      className="flex items-center gap-2 p-3 rounded-lg border bg-muted/50 hover:bg-muted transition-colors text-left"
                    >
                      <Folder className="h-5 w-5 text-primary shrink-0" />
                      <span className="text-sm font-medium truncate">{folder.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="mt-4">
            <CardHeader>
              <CardTitle className="text-base">Informações</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total de pastas</span>
                <Badge variant="outline">{folders.length}</Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subpastas aqui</span>
                <Badge variant="outline">{children.length}</Badge>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
